import { LibelleLogement } from './../../../constants/libelle-logement';
import { FormGroup, ControlContainer, Validators, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, AfterContentChecked, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { InfoLongementForm, LogementModel } from './models/logement.model';
import { ConstantePage } from './../../../constants/constante-page';
import { ConstantesEligibilite } from './../../../constants/constante-eligibilite';
import { TagWaService } from '../../../services/tag-wa.service';
import { CcdStringUtils } from '../../../services/ccd-string.utils';
import { CcdIntervalleModel, CcdLocaliteModel } from '@cnaf.usagers.appli/cnaf-commons-dral';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { EnfantService } from '../../../02-declaration-enfant/components/enfant/services/enfant.service';
import { log } from 'console';


@Component({
  selector: 'app-info-logement',
  templateUrl: './info-logement.component.html'
})
export class InfoLogementComponent implements OnInit, OnDestroy, AfterContentChecked {

  @Input() logementModel: LogementModel;
  
  @Input() name : string;

  // renvoie le bean LogementModel
  @Output() logementModelEvent = new EventEmitter<LogementModel>();

  localite : CcdLocaliteModel = new CcdLocaliteModel();  
  lieuResidenceValue = "";
  typeOccupationValue = "";
  typeCrousValue = "";
  estMeubleValue = "";
  mttDeclareValue = "";
  estConventionneValue = "";
  datePrPrincipaleValue = null;
  estFinanceValue ="";
  mttMensualiteValue ="";
  flagDateChanged: boolean = false; 

  isMetropole = true;
  afficheBlocLieuResidence = false;
  parentForm : FormGroup;
  logementForm : FormGroup<InfoLongementForm>;

  // Tableaux de constantes pour gérer les questions sur le logement
  libelleLogement = LibelleLogement;

  // Subscribers
  subscribers : Array<Subscription> = new Array();
  
  declencherModaleNonEligible = false;

  listeExclusion : Array<CcdIntervalleModel>;

  validatorsMontantLoyer = [Validators.required, Validators.pattern("^([1-9][0-9]*)$"), this.validationSup3000()];
  mapErreursMontantLoyer : Map<string, string> = new Map(
    [
      ["pattern", "Ce montant doit être supérieur à zéro sans virgule"],
      ["required", "Champ obligatoire"],
      ["errSup3000", "Ce montant ne peut pas être supérieur à 3000"]
    ]
  );

  validatorsMontantPret = [Validators.required, Validators.pattern("^([1-9][0-9]*)$"), this.validationMensualiteSup3000()];
  mapErreursMontantPret : Map<string, string> = new Map(
    [
      ["pattern", "Ce montant doit être supérieur à zéro sans virgule"],
      ["required", "Champ obligatoire"],
      ["errSup3000", "Ce montant ne peut pas être supérieur à 3000"]
    ]
  );

  datePrincipaleValidator = [Validators.required, this.dateBefore1900Validator(), this.dateWithinSixMonthsValidator()];
  mapErreurDate : Map<string, string> = new Map(
    [
      ["required", "Champ obligatoire"],
      ["dateBefore1900", "Format de date invalide. L'année de signature du prêt ne doit pas être inférieur à 1900"],
      ["dateTooOld", "La date de signature du prêt doit être inférieure à 6 mois"]
    ]
  );

  constructor(
    private readonly controlContainer: ControlContainer,
    private readonly router: Router,
    private readonly tagWaService: TagWaService,
    private readonly cd: ChangeDetectorRef,
    private readonly enfantService: EnfantService
  ) {}

  ngOnInit(): void {
    this.logementForm = new FormGroup<InfoLongementForm>({});
    this.listeExclusion = ConstantesEligibilite.INTERVALLES_CODES_POSTAUX_NON_ELIGIBLES;
    this.parentForm = this.controlContainer?.control as FormGroup;
    this.parentForm.addControl(this.name, this.logementForm);
    if (this.logementModel.localite.codePostal !== '') {
      this.mapSessionData();
      this.afficheBlocLieuResidence = true;
    } 
    
    this.subscribers.push(this.logementForm.valueChanges.subscribe(dataChange => {
      this.bindingLogementModel();
      this.isMetropole = this.enfantService.estMetropole( Number(this.localite?.codePostal) );
    }));
  }

  ngAfterContentChecked() : void {
    this.cd.detectChanges();
  }
  
  gestionFermetureModale() : void {
    this.localite = new CcdLocaliteModel();
    this.declencherModaleNonEligible = false;
  }

  mapSessionData() : void {    
    this.localite = this.logementModel.localite;
    this.lieuResidenceValue = this.logementModel.lieuResidence;
    this.typeOccupationValue = this.logementModel.typeOccupation;
    this.typeCrousValue = this.logementModel.typeCrous;
    this.estMeubleValue = this.logementModel.estMeuble;
    this.estConventionneValue = this.logementModel.estConventionne;
    this.mttDeclareValue = CcdStringUtils.numberToString(this.logementModel.mttDeclare);
    this.datePrPrincipaleValue = CcdStringUtils.formatDateToString(this.logementModel.datePrPrincipale);
    this.estFinanceValue = this.logementModel.estFinance;
    this.mttMensualiteValue = CcdStringUtils.numberToString(this.logementModel.mttMensualite);

    this.logementForm.markAsDirty();
  }

  ngOnDestroy(): void {
    this.subscribers.forEach(sub => sub.unsubscribe());

    this.parentForm.removeControl(this.name);
  }

  bindingLogementModel() {
    this.logementModel = new LogementModel(
      this.localite,
      this.lieuResidenceValue, // = cdTypeLogement
      this.typeOccupationValue,
      this.typeCrousValue,
      this.estMeubleValue,
      CcdStringUtils.stringToNumber(this.mttDeclareValue),
      this.estConventionneValue,
      CcdStringUtils.parseDateFromString(this.datePrPrincipaleValue),
      this.estFinanceValue,
      CcdStringUtils.stringToNumber(this.mttMensualiteValue)
    );
    this.logementModelEvent.emit(this.logementModel);
  }

  /**
   * Détermine le libellé du montant (loyer / redevance ...)
   */
  getLibelleMontant() : string {
    let libelle = "";
      if (this.lieuResidenceValue === 'LOGEMENT_CROUS') {
      libelle = "Montant mensuel de votre loyer";

    } else if (['APPARTEMENT_OU_MAISON', 'CHAMBRE'].includes(this.lieuResidenceValue)) {
        if (this.typeOccupationValue === 'LOCATION') {
        libelle = "Montant mensuel de votre loyer "
      } else {
        libelle = "Part de votre loyer mensuel ";
      }
        if (this.estMeubleValue === 'true') {
        libelle = libelle.concat("(charges comprises)");
      } else {
        libelle = libelle.concat("(hors charges)");
      }
    }
    else {
      libelle = "Montant mensuel de votre redevance";
    }
    return libelle;
  }

  /**
   * Methode pour taguer un clic
   * 
   * Tag de la popup Nature de logement
   */
  clickPopup() : void {
    // On tague la popup Nature de logement pour le WA
    this.tagWaService.tagClic(ConstantePage.STATS_CLIC_NATURE_LOGEMENT, ConstantePage.STATS_PAGE_DECLARATION);   
  }

  /**
   * Redirection vers la page d'erreur technique qi erreur HTTP du composant code postal
   * @param $err 
   */
  gerrerErreursCodePostal($err : HttpErrorResponse) {
    this.router.navigateByUrl('/page-erreur-technique');
  }

  /**
   * Validator qui envoie une erreur si le montant du loyer est supérieur à 3000
   */
  validationSup3000(): ValidatorFn {
    return (controls: FormGroup): ValidationErrors | null => {
      const montant = CcdStringUtils.stringToNumber(this.mttDeclareValue);
      const isSup3000 = montant && (montant > 3000);
      let result = null;
      if (isSup3000) {
        result = {errSup3000 : true};
      }
      return result;
    };
  }

  /**
   * Validator qui envoie une erreur si le montant de la mensualite est supérieur à 3000
   */
  validationMensualiteSup3000(): ValidatorFn {
    return (controls: FormGroup): ValidationErrors | null => {
      const mensualite = CcdStringUtils.stringToNumber(this.mttMensualiteValue);
      const isSup3000 = mensualite && (mensualite > 3000);
      let result = null;
      if (isSup3000) {
        result = {errSup3000 : true};
      }
      return result;
    };
  }

  /**
   * Validator qui envoie une erreur si l'année est avant 1900'
   */
  dateBefore1900Validator(): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      const value = this.datePrPrincipaleValue;
      let result = null;
      if (value) {
        const year = new Date(value).getFullYear();
        if (year < 1900) {
          result = { 'dateBefore1900': true };
        }
        
        return result;
      }
    };
  }

  /**
   * Validator qui envoie une erreur si la date saisie est supérieur à 6 mois de la date du jour'
   */
  dateWithinSixMonthsValidator(): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {

      const currentDate = new Date();
      currentDate.setMonth(currentDate.getMonth() + 6);
      currentDate.setHours(0,0,0,0);
      const inputDate = this.logementModel.datePrPrincipale;
      let result = null;
      if (this.isValidDate()) {
        if (inputDate && ((inputDate.getTime() >= currentDate.getTime()))) {
          result = { 'dateTooOld': true };
        }
      }
      return result;
    };
  }

  isCodePostalDOM(codePostal: string): boolean {
    const code = parseInt(codePostal, 10);

    if (isNaN(code) || codePostal.length !== 5) {
      return false;
    }

    if ((codePostal.startsWith("971") && code >= 97100 && code <= 97199) ||
        (codePostal.startsWith("972") && code >= 97200 && code <= 97299) ||
        (codePostal.startsWith("973") && code >= 97300 && code <= 97399) ||
        (codePostal.startsWith("974") && code >= 97400 && code <= 97499) ||
        (codePostal.startsWith("976") && code >= 97600 && code <= 97699)) {
      return true;
    }

    return false;
  }

  // Variable qui détermine si le code postal est un DOM
  get isDOM(): boolean {
    return this.isCodePostalDOM(this.localite.codePostal);
  }

  isValidDate(): boolean {
    const date = this.logementModel.datePrPrincipale;
    // Vérifier que l'objet est une instance de Date et que la date n'est pas invalide
    if(date instanceof Date) {
      date.setHours(0, 0, 0, 0);
        return true;
    }
    return false;
  }

  // Fonction pour vérifier si la date sélectionnée est antérieur au 01/02/2018
  isDateAnterieur(): boolean {

    if (this.isValidDate()) {
     const dateLimite = new Date('2018-02-01');

      // Créer une nouvelle date à partir de datePrincipaleValue, mais sans l'heure
      const datePrincipaleSansHeure = new Date(this.logementModel.datePrPrincipale);
      dateLimite.setHours(0, 0, 0, 0);
      datePrincipaleSansHeure.setHours(0, 0, 0, 0);
      return datePrincipaleSansHeure < dateLimite;
    } 
  }

  // Fonction pour vérifier si la date sélectionnée est postérieur au 31/12/2019
  isDatePosterieur(): boolean {
    if (this.isValidDate()) {
    const dateLimite = new Date('2019-12-31');

      // Créer une nouvelle date à partir de datePrincipaleValue, mais sans l'heure
      const datePrincipaleSansHeure = new Date(this.logementModel.datePrPrincipale);
      dateLimite.setHours(0, 0, 0, 0);
      datePrincipaleSansHeure.setHours(0, 0, 0, 0);
      return datePrincipaleSansHeure > dateLimite;
    }
  }

  isDateAvant1900(): boolean {
    if (this.isValidDate()) {
      // Créer une nouvelle date à partir de datePrincipaleValue, mais sans l'heure
      const datePrincipaleSansHeure = new Date(this.logementModel.datePrPrincipale);
      const year = new Date(datePrincipaleSansHeure).getFullYear();
        if (year < 1900) {
          return true;
        }
    return false;
    }
  }

  // Méthode appelée lorsqu'une nouvelle date est sélectionnée
  onDateChange(newDate: any): void {
    // Mettre à jour la valeur de la date
    this.datePrPrincipaleValue = newDate;

    // Signaler que la date a changé
    this.flagDateChanged = true;

    // Réinitialiser les autres champs si nécessaire
    this.resetFieldsAfterDateChange();
  }

  // Réinitialiser les valeurs des champs après un changement de date
  resetFieldsAfterDateChange(): void {
    if (this.flagDateChanged) {
      this.mttMensualiteValue = null;
      this.estFinanceValue = null;
      this.flagDateChanged = false;
    }
  }
  
}
